import apiClient, { handleApiError } from './apiClient';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  rol: 'cliente' | 'admin' | 'repartidor';
  profile: {
    telefono: string;
    direccion: string;
    fecha_nacimiento: string | null;
    dni: string;
    avatar: string | null;
    activo: boolean;
    rol: 'cliente' | 'admin' | 'repartidor';
    vehiculo_tipo?: string;
    placa_vehiculo?: string;
    zona_asignada?: string;
    en_servicio?: boolean;
    latitud_actual?: number;
    longitud_actual?: number;
    nombre_completo?: string;
    es_cliente?: boolean;
    es_admin?: boolean;
    es_repartidor?: boolean;
  };
  nombre_completo: string;  // Viene del backend en nivel superior
  es_cliente: boolean;       // Viene del backend en nivel superior
  es_admin: boolean;         // Viene del backend en nivel superior
  es_repartidor: boolean;    // Viene del backend en nivel superior
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  password2: string;
  first_name: string;
  last_name: string;
  telefono?: string;
  direccion?: string;
  rut?: string;
  rol?: 'cliente' | 'admin' | 'repartidor';
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
  message?: string;
}

export interface LoginResponse extends AuthResponse {}

export interface ChangePasswordData {
  old_password: string;
  new_password: string;
  new_password2: string;
}

class AuthService {
  async loginWithGoogle(googleToken: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/google/login/', { token: googleToken });
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
  
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login/', credentials);
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      // Usar timeout más largo para registro (60 segundos)
      const response = await apiClient.post<AuthResponse>('/auth/register/', data, {
        timeout: 60000
      });
      localStorage.setItem('access_token', response.data.access);
      localStorage.setItem('refresh_token', response.data.refresh);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data;
    } catch (error: any) {
      console.error('Error en authService.register:', error);
      // Si es error de red/timeout, dar mensaje más claro
      if (error.code === 'ECONNABORTED' || !error.response) {
        throw new Error('El servidor tardó demasiado en responder. Por favor intenta de nuevo.');
      }
      throw new Error(handleApiError(error));
    }
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await apiClient.post('/auth/logout/', { refresh: refreshToken });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch (error) {
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  async getProfile(): Promise<User> {
    try {
      const response = await apiClient.get<User>('/auth/profile/');
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateProfile(data: Partial<User['profile']>): Promise<{ user: User; message: string }> {
    try {
      const response = await apiClient.put<{ user: User; message: string }>('/auth/profile/', data);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    try {
      const response = await apiClient.post<{ message: string }>('/auth/change-password/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async checkUsername(username: string): Promise<{ username: string; available: boolean }> {
    try {
      const response = await apiClient.get<{ username: string; available: boolean }>(
        `/auth/check-username/?username=${username}`
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async checkEmail(email: string): Promise<{ email: string; available: boolean }> {
    try {
      const response = await apiClient.get<{ email: string; available: boolean }>(
        `/auth/check-email/?email=${email}`
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async checkAuth(): Promise<{ authenticated: boolean; user: User }> {
    try {
      const response = await apiClient.get<{ authenticated: boolean; user: User }>('/auth/check-auth/');
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getRepartidoresDisponibles(): Promise<User[]> {
    try {
      const response = await apiClient.get<User[]>('/auth/repartidores/disponibles/');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getTodosRepartidores(): Promise<User[]> {
    try {
      const response = await apiClient.get<User[]>('/auth/repartidores/todos/');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async actualizarUbicacionRepartidor(data: {
    latitud: number;
    longitud: number;
    en_servicio?: boolean;
  }): Promise<{ message: string; repartidor: User }> {
    try {
      const response = await apiClient.post<{ message: string; repartidor: User }>(
        '/auth/repartidores/ubicacion/',
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getUsuarios(): Promise<User[]> {
    try {
      const response = await apiClient.get<User[]>('/auth/usuarios/');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async refreshToken(): Promise<string> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token');
      }
      const response = await apiClient.post<{ access: string }>('/auth/token/refresh/', {
        refresh: refreshToken
      });
      const { access } = response.data;
      localStorage.setItem('access_token', access);
      return access;
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      throw new Error('Token expirado');
    }
  }
}

const authService = new AuthService();
export { authService };
export default authService;
